import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body) {
  if (!body?.success) {
    throw new Error(sanitizeBusinessMessage(body?.message, 'Request failed'))
  }
  return body.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type CategoryRow = {
  id: number | null
  slug: string
  name: string
  source: string
  image: string | null
  /** Present for `source === 'store'`; orphan rows may omit. */
  isActive?: boolean
  /** Present for merchant-defined categories; lower sorts first on the storefront. */
  sortOrder?: number
  /** Number of products assigned to this category. */
  productCount?: number
}

export type CategoriesListResponse = {
  defaultStoreId: number | null
  items: CategoryRow[]
  page: number
  limit: number
  total: number
}

export type CategoryDetail = {
  defaultStoreId: number
  id: number | null
  slug: string
  name: string
  source: 'store' | 'product' | string
  image: string | null
  isActive?: boolean
  sortOrder?: number | null
  productCount: number
}

export function categoryDetailPath(row: Pick<CategoryRow, 'id' | 'slug'>): string {
  if (row.id != null) return `/categories/${row.id}`
  return `/categories/${encodeURIComponent(row.slug)}`
}

export async function fetchCategories(params?: {
  page?: number
  limit?: number
  q?: string
}): Promise<CategoriesListResponse> {
  try {
    const { data } = await api.get('/storebusiness/categories', {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
        ...(params?.q ? { q: params.q } : {}),
      },
    })
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to load categories')
  }
}

export async function fetchCategoryByRef(idOrSlug: string | number): Promise<CategoryDetail> {
  try {
    const ref = encodeURIComponent(String(idOrSlug))
    const { data } = await api.get(`/storebusiness/categories/${ref}`)
    return assertSuccess(data) as CategoryDetail
  } catch (e) {
    throw apiError(e, 'Failed to load category')
  }
}

export type CreateCategoryResult = {
  id: number
  slug: string
  name: string
  image: string | null
  isActive?: boolean
  sortOrder?: number
  /** Present when create had no image and the API started AI image generation (poll job). */
  aiImageJobId?: string | null
}

export async function createCategory(payload: {
  name: string
  slug?: string
  image?: string | null
  sortOrder?: number
}): Promise<CreateCategoryResult> {
  try {
    const { data } = await api.post('/storebusiness/categories', payload)
    return assertSuccess(data) as CreateCategoryResult
  } catch (e) {
    throw apiError(e, 'Failed to create category')
  }
}

export async function updateCategory(
  id: number,
  payload: { name?: string; slug?: string; image?: string | null; isActive?: boolean; sortOrder?: number },
) {
  try {
    const { data } = await api.patch(`/storebusiness/categories/${id}`, payload)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to update category')
  }
}

export async function deleteCategory(id: number) {
  try {
    const { data } = await api.delete(`/storebusiness/categories/${id}`)
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to delete category')
  }
}
